import React, { useState, useEffect } from "react";
import { Form, InputNumber, Button, Card, Typography, Divider, Spin, Row, Col } from "antd";
import SettingsServ from "../../service/SettingsServ";
import Notify from "../../util/Notify";
import Breadcrumbs from "../../component/Breadcrumbs";
import { IconGift, IconUsers, IconShoppingBag, IconCircleCheck } from '@tabler/icons-react';

const { Title, Text } = Typography;

const ReferralSettings = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);

    const fetchSettings = async () => {
        setPageLoading(true);
        try {
            const [regRes, refRes, invRes] = await Promise.all([
                SettingsServ.get("registration_bonus"),
                SettingsServ.get("referral_bonus_referrer"),
                SettingsServ.get("referral_bonus_invitee")
            ]);

            form.setFieldsValue({
                registration_bonus: regRes?.data ?? 50,
                referral_bonus_referrer: refRes?.data ?? 100,
                referral_bonus_invitee: invRes?.data ?? 100,
            });
        } catch (error) {
            console.error("Error fetching referral settings:", error);
            // Default values if settings not found
            form.setFieldsValue({
                registration_bonus: 50,
                referral_bonus_referrer: 100,
                referral_bonus_invitee: 100,
            });
        } finally {
            setPageLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, [form]);

    const regBonus = Form.useWatch('registration_bonus', form);
    const refBonus = Form.useWatch('referral_bonus_referrer', form);
    const invBonus = Form.useWatch('referral_bonus_invitee', form);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            await Promise.all([
                SettingsServ.update("registration_bonus", { value: values.registration_bonus, description: "Welcome bonus for all new users" }),
                SettingsServ.update("referral_bonus_referrer", { value: values.referral_bonus_referrer, description: "Bonus for the user who refers someone" }),
                SettingsServ.update("referral_bonus_invitee", { value: values.referral_bonus_invitee, description: "Extra bonus for the user who joins via referral code" })
            ]);
            Notify.success("Referral & Rewards settings updated successfully");
        } catch (error) {
            Notify.error("Failed to update settings");
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="sd-container" style={{ padding: '24px' }}>
            <div className="pcu-margin-bottom-20">
                <Breadcrumbs />
            </div>

            <div className="sd-header" style={{ marginBottom: '32px', background: 'transparent', padding: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ 
                        width: '56px', 
                        height: '56px', 
                        background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)', 
                        borderRadius: '16px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        boxShadow: '0 8px 16px rgba(99, 102, 241, 0.2)' 
                    }}>
                        <IconGift size={28} color="white" />
                    </div>
                    <div>
                        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 800, color: '#1e293b', letterSpacing: '-0.3px' }}>Rewards & Referral Settings</h1>
                        <p style={{ margin: 0, fontSize: '13px', color: '#64748b', fontWeight: 500 }}>Configure global incentive programs and referral bonuses</p>
                    </div>
                </div>
            </div>

            <Spin spinning={pageLoading}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                >
                    <Row gutter={[32, 32]}>
                        {/* Left Column: Configuration Form */}
                        <Col xs={24} lg={15}>
                            <div style={{ 
                                background: '#fff', 
                                border: '1px solid #f1f5f9', 
                                borderRadius: '24px', 
                                padding: '32px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.02)'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
                                    <div style={{ width: '8px', height: '24px', background: '#6366f1', borderRadius: '4px' }}></div>
                                    <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#1e293b' }}>Earning Rules</h2>
                                </div>

                                <div style={{ 
                                    padding: '24px', 
                                    background: 'linear-gradient(to right, #f8fafc, #ffffff)', 
                                    borderRadius: '16px', 
                                    border: '1px solid #f1f5f9',
                                    marginBottom: '24px'
                                }}>
                                    <Form.Item
                                        name="registration_bonus"
                                        label={<span style={{ fontWeight: 700, color: '#1e293b', fontSize: '14px' }}>Welcome Bonus (Global)</span>}
                                        rules={[{ required: true, message: 'Required' }]}
                                        extra={<span style={{ fontSize: '12px', color: '#94a3b8' }}>Automatically credited to every new user who creates an account.</span>}
                                    >
                                        <InputNumber
                                            min={0}
                                            style={{ width: "100%", height: '48px', borderRadius: '12px' }}
                                            prefix={<IconGift size={20} color="#6366f1" style={{ marginRight: '8px' }} />}
                                            addonAfter={<span style={{ fontWeight: 700, color: '#6366f1' }}>Points</span>}
                                            placeholder="50"
                                        />
                                    </Form.Item>
                                </div>

                                <Row gutter={24}>
                                    <Col span={12}>
                                        <div style={{ 
                                            padding: '24px', 
                                            background: 'linear-gradient(to bottom, #fff7ed, #ffffff)', 
                                            borderRadius: '16px', 
                                            border: '1px solid #ffedd5',
                                            height: '100%'
                                        }}>
                                            <Form.Item
                                                name="referral_bonus_referrer"
                                                label={<span style={{ fontWeight: 700, color: '#1e293b', fontSize: '14px' }}>Referrer Reward</span>}
                                                rules={[{ required: true, message: 'Required' }]}
                                                extra={<span style={{ fontSize: '12px', color: '#94a3b8' }}>Bonus for the person sharing code.</span>}
                                            >
                                                <InputNumber
                                                    min={0}
                                                    style={{ width: "100%", height: '48px', borderRadius: '12px' }}
                                                    prefix={<IconUsers size={20} color="#f59e0b" style={{ marginRight: '8px' }} />}
                                                    addonAfter={<span style={{ fontWeight: 700, color: '#f59e0b' }}>Pts</span>}
                                                    placeholder="100"
                                                />
                                            </Form.Item>
                                        </div>
                                    </Col>
                                    <Col span={12}>
                                        <div style={{ 
                                            padding: '24px', 
                                            background: 'linear-gradient(to bottom, #f0fdf4, #ffffff)', 
                                            borderRadius: '16px', 
                                            border: '1px solid #dcfce7',
                                            height: '100%'
                                        }}>
                                            <Form.Item
                                                name="referral_bonus_invitee"
                                                label={<span style={{ fontWeight: 700, color: '#1e293b', fontSize: '14px' }}>Invitee Bonus</span>}
                                                rules={[{ required: true, message: 'Required' }]}
                                                extra={<span style={{ fontSize: '12px', color: '#94a3b8' }}>Extra points for joining via code.</span>}
                                            >
                                                <InputNumber
                                                    min={0}
                                                    style={{ width: "100%", height: '48px', borderRadius: '12px' }}
                                                    prefix={<IconShoppingBag size={20} color="#10b981" style={{ marginRight: '8px' }} />}
                                                    addonAfter={<span style={{ fontWeight: 700, color: '#10b981' }}>Pts</span>}
                                                    placeholder="50"
                                                />
                                            </Form.Item>
                                        </div>
                                    </Col>
                                </Row>

                                <div style={{ marginTop: '40px' }}>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={loading}
                                        style={{ 
                                            height: '56px', 
                                            fontSize: '16px', 
                                            fontWeight: 800, 
                                            borderRadius: '16px',
                                            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                                            border: 'none',
                                            boxShadow: '0 10px 20px rgba(99, 102, 241, 0.2)',
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '12px'
                                        }}
                                    >
                                        <IconCircleCheck size={22} />
                                        Save Configuration
                                    </Button>
                                </div>
                            </div>
                        </Col>

                        {/* Right Column: Visual Summary & Info */}
                        <Col xs={24} lg={9}>
                            <div style={{ 
                                background: 'radial-gradient(circle at top right, #4338ca 0%, #1e1b4b 100%)', 
                                borderRadius: '24px', 
                                padding: '32px',
                                color: '#fff',
                                height: '100%',
                                boxShadow: '0 20px 40px rgba(30, 27, 75, 0.25), inset 0 0 40px rgba(99, 102, 241, 0.1)',
                                position: 'relative',
                                overflow: 'hidden',
                                border: '1px solid rgba(255,255,255,0.1)'
                            }}>
                                {/* Radiant Glow Elements */}
                                <div style={{ position: 'absolute', top: '-20%', right: '-20%', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%)', borderRadius: '50%' }}></div>
                                <div style={{ position: 'absolute', bottom: '10%', left: '-10%', width: '200px', height: '200px', background: 'radial-gradient(circle, rgba(168, 85, 247, 0.1) 0%, transparent 70%)', borderRadius: '50%' }}></div>

                                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#fff', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <IconCircleCheck size={20} color="#818cf8" /> Program Overview
                                </h2>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                    <div style={{ 
                                        padding: '24px', 
                                        background: 'rgba(255,255,255,0.05)', 
                                        borderRadius: '20px', 
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        backdropFilter: 'blur(10px)'
                                    }}>
                                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#818cf8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>SCENARIO A: STANDARD</div>
                                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#e2e8f0', lineHeight: 1.5 }}>
                                            User registers normally without a referral code.
                                        </div>
                                        <div style={{ marginTop: '16px', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                                            <span style={{ fontSize: '32px', fontWeight: 800, color: '#fff' }}>{regBonus || 50}</span>
                                            <span style={{ fontSize: '14px', fontWeight: 500, color: '#94a3b8' }}>Points awarded</span>
                                        </div>
                                    </div>

                                    <div style={{ 
                                        padding: '24px', 
                                        background: 'rgba(255,255,255,0.05)', 
                                        borderRadius: '20px', 
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        backdropFilter: 'blur(10px)'
                                    }}>
                                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#fbbf24', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px' }}>SCENARIO B: REFERRAL</div>
                                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#e2e8f0', lineHeight: 1.5 }}>
                                            User joins via a friend's referral link or code.
                                        </div>
                                        
                                        <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span style={{ fontSize: '13px', color: '#94a3b8' }}>Invitee gets</span>
                                                <span style={{ fontSize: '16px', fontWeight: 800, color: '#fff' }}>
                                                    {(Number(regBonus) || 50) + (Number(invBonus) || 50)} Pts
                                                </span>
                                            </div>
                                            <div style={{ height: '1px', background: 'rgba(255,255,255,0.05)' }}></div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span style={{ fontSize: '13px', color: '#94a3b8' }}>Referrer gets</span>
                                                <span style={{ fontSize: '16px', fontWeight: 800, color: '#fbbf24' }}>
                                                    {refBonus || 100} Pts
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ 
                                    marginTop: '5px', 
                                    padding: '16px', 
                                    background: 'rgba(236, 253, 245, 0.05)', 
                                    borderRadius: '16px', 
                                    border: '1px dashed rgba(16, 185, 129, 0.2)',
                                    display: 'flex',
                                    gap: '12px',
                                    alignItems: 'start'
                                }}>
                                    <IconCircleCheck size={18} color="#10b981" style={{ marginTop: '2px' }} />
                                    <p style={{ margin: 0, fontSize: '11px', color: '#9ca3af', lineHeight: 1.5 }}>
                                        Points are automatically managed by the system and credited to user wallets upon successful registration.
                                    </p>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Form>
            </Spin>

            <style dangerouslySetInnerHTML={{
                __html: `
                .ant-input-number-affix-wrapper {
                    border: 1.5px solid #f1f5f9 !important;
                    transition: all 0.3s ease !important;
                }
                .ant-input-number-affix-wrapper-focused {
                    border-color: #6366f1 !important;
                    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1) !important;
                }
                .ant-input-number-group-addon {
                    background: #f8fafc !important;
                    border: 1.5px solid #f1f5f9 !important;
                    border-left: none !important;
                    border-radius: 0 12px 12px 0 !important;
                }
            `}} />
        </div>
    );
};


export default ReferralSettings;
